import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Polyline, Polygon as LeafletPolygon, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { ArrowLeft, Loader2, Map as MapIcon, Satellite, Layers, Gift, User, LogOut, Shield, Target, Search, Star, Eye, Waypoints, Video, VideoOff, RotateCcw } from "lucide-react";
import HintTimerOverlay from "./HintTimerOverlay";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { getDeviceFingerprint } from "@/services/FingerprintService";
import { useVideo } from "@/contexts/VideoContext";
import Avatar from "./Avatar";
import LanguageSwitcher from "./LanguageSwitcher";
import GameWinnersStrip from "./GameWinnersStrip";
import "leaflet/dist/leaflet.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Helper to get translated text
const getTranslatedText = (translations, lang, fallback) => {
  if (!translations) return fallback;
  if (translations[lang]) return translations[lang];
  if (translations['ru']) return translations['ru'];
  return fallback;
};


// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom marker icons
const guessIcon = new L.Icon({
  iconUrl: "data:image/svg+xml;base64," + btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path fill="#0a4797" d="M12.5 0C5.596 0 0 5.596 0 12.5c0 1.996.471 3.889 1.309 5.574L12.5 41l11.191-22.926C24.529 16.389 25 14.496 25 12.5 25 5.596 19.404 0 12.5 0z"/><circle fill="white" cx="12.5" cy="12.5" r="5"/></svg>`),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const actualIcon = new L.Icon({
  iconUrl: "data:image/svg+xml;base64," + btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path fill="#22c55e" d="M12.5 0C5.596 0 0 5.596 0 12.5c0 1.996.471 3.889 1.309 5.574L12.5 41l11.191-22.926C24.529 16.389 25 14.496 25 12.5 25 5.596 19.404 0 12.5 0z"/><circle fill="white" cx="12.5" cy="12.5" r="5"/></svg>`),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Prevent map drag on first click by capturing mousedown and stopping propagation
const MapDragBlocker = ({ active }) => {
  useMapEvents({
    mousedown: (e) => {
      if (active) {
        e.originalEvent && e.originalEvent.stopPropagation && e.originalEvent.stopPropagation();
        e.originalEvent && e.originalEvent.preventDefault && e.originalEvent.preventDefault();
      }
    }
  });
  return null;
};

const MapClickHandler = ({ onMapClick, disabled }) => {
  useMapEvents({
    click: (e) => {
      if (!disabled) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

const MapRefHandler = ({ mapRef, onMapMove }) => {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      onMapMove(center, zoom);
    },
  });
  
  useEffect(() => {
    if (mapRef) {
      mapRef.current = map;
    }
  }, [map, mapRef]);
  
  return null;
};

const MapViewController = ({ mapExpanded, result, showOriginalPoint, savedCenter, savedZoom }) => {
  const map = useMapEvents({});
  const didApplyExpand = useRef(false);
  const didFitResult = useRef(null);

  // Initial invalidateSize on mount
  useEffect(() => {
    if (!map) return;
    
    // Invalidate immediately on mount
    setTimeout(() => {
      try { 
        map.invalidateSize({ pan: false });
      } catch (e) {
        console.error('Map invalidateSize error:', e);
      }
    }, 50);
    
    // And again after a delay to ensure proper rendering
    setTimeout(() => {
      try { 
        map.invalidateSize({ pan: false });
      } catch (e) {}
    }, 300);
  }, [map]);

  useEffect(() => {
    if (!map) return;
    if (mapExpanded && !didApplyExpand.current) {
      // Apply size invalidation AFTER CSS transition completes (400ms)
      didApplyExpand.current = true;
      
      // Only set default view if there's no result to show
      // If there's a result, fitBounds will handle the view
      if (!result) {
        const targetCenter = savedCenter || [55.7558, 37.6173];
        const targetZoom = savedZoom || 3;
        
        setTimeout(() => {
          try { 
            map.invalidateSize({ pan: false, animate: false });
            map.setView(targetCenter, targetZoom, { animate: false });
          } catch (e) {}
        }, 500);
      } else {
        // Just invalidate size, let fitBounds handle the view
        setTimeout(() => {
          try { 
            map.invalidateSize({ pan: false, animate: false });
          } catch (e) {}
        }, 500);
      }
    }
    if (!mapExpanded) {
      // Reset flags when minimized again
      didApplyExpand.current = false;
      didFitResult.current = null;
      
      // Keep the saved position (don't reset to default)
      setTimeout(() => {
        try { 
          map.invalidateSize({ pan: false });
          // Don't change the view - keep current position
        } catch (e) {}
      }, 50);
    }
  }, [mapExpanded, map, savedCenter, savedZoom, result]);

  // Fit map to show both markers when result is available
  useEffect(() => {
    if (!map || !mapExpanded || !result) return;
    
    const hasValidCoords = 
      typeof result.guess_lat === 'number' &&
      typeof result.guess_lng === 'number' &&
      typeof result.actual_lat === 'number' &&
      typeof result.actual_lng === 'number';
    
    if (!hasValidCoords) return;
    
    // Only fit once per result, but reset when showOriginalPoint changes
    const fitKey = `${result.guess_lat}-${result.actual_lat}-${showOriginalPoint}`;
    if (didFitResult.current === fitKey) return;
    didFitResult.current = fitKey;
    
    // Wait for map to be fully ready
    const doFitBounds = () => {
      try {
        // Ensure map size is calculated correctly
        map.invalidateSize();
        
        if (!showOriginalPoint) {
          // Only show guess marker location
          map.setView([result.guess_lat, result.guess_lng], 10);
        } else {
          // Show both markers with line between them
          const bounds = L.latLngBounds(
            [result.guess_lat, result.guess_lng],
            [result.actual_lat, result.actual_lng]
          );
          // Fit bounds to show both markers
          map.fitBounds(bounds, { 
            padding: [50, 50],
            animate: false
          });
        }
      } catch (e) {
        console.error('Error fitting map bounds:', e);
      }
    };
    
    // Multiple attempts to ensure it works
    setTimeout(doFitBounds, 100);
    setTimeout(doFitBounds, 300);
    setTimeout(doFitBounds, 600);
  }, [map, mapExpanded, result, showOriginalPoint]);

  return null;
};

const HintPolygonIcon = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="8,1.5 17,3 22,10 19,20 9,22 2,14" fill="none" />
    <circle cx="8" cy="1.5" r="2" fill={color} stroke="none" />
    <circle cx="17" cy="3" r="2" fill={color} stroke="none" />
    <circle cx="22" cy="10" r="2" fill={color} stroke="none" />
    <circle cx="19" cy="20" r="2" fill={color} stroke="none" />
    <circle cx="9" cy="22" r="2" fill={color} stroke="none" />
    <circle cx="2" cy="14" r="2" fill={color} stroke="none" />
  </svg>
);

const GuessGame = () => {
  const { gameId, panoramaId } = useParams();
  const navigate = useNavigate();

  // Detect document-level fullscreen. We don't enter FS from here — the player
  // already opened FS on <html> before navigating. We just style the panorama
  // page to fill the FS viewport.
  const [isFullscreen, setIsFullscreen] = useState(typeof document !== 'undefined' ? !!document.fullscreenElement : false);
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);
  useEffect(() => {
    if (!isFullscreen) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [isFullscreen]);

  const { user, getAuthHeader, hasRole, logout } = useAuth();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const { isActive: videoActive, mode: videoMode, miniHidden, setMiniHidden, seek: videoSeek, timecodes, startVideoAtTime, currentTime: videoCurrentTime } = useVideo();
  const [panorama, setPanorama] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [winners, setWinners] = useState([]);
  const [showMessage, setShowMessage] = useState(true);
  const [gameTimecodes, setGameTimecodes] = useState([]);
  const [gameVideoUrl, setGameVideoUrl] = useState(null);
  // Fallback: load from my-results if my-answer is 404/empty
  const fetchAnswerFallback = async (pano, currentUser) => {
    try {
      if (!pano.game_id || !currentUser) return null;
      const r = await axios.get(`${API}/games/${pano.game_id}/my-results`, { headers: getAuthHeader() });
      if (!r.data || !r.data.answers) return null;
      const ans = r.data.answers.find(a => a.panorama_id === pano.id);
      if (!ans) return null;
      return {
        has_answer: true,
        points: ans.score,
        distance: ans.distance,
        guess_lat: ans.guess_lat,
        guess_lng: ans.guess_lng,
        actual_lat: pano.latitude,
        actual_lng: pano.longitude,
        guessed_at: ans.guessed_at
      };
    } catch (e) {
      return null;
    }
  };

  const [guessMarker, setGuessMarker] = useState(null);
  const [result, setResult] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('map') === '1') return true;
    if (params.get('hint_sig')) return true;
    // Check sessionStorage flag (set by WP quiz redirect)
    if (sessionStorage.getItem('hint_open_map')) {
      sessionStorage.removeItem('hint_open_map');
      return true;
    }
    return false;
  });
  const [mapType, setMapType] = useState("streets");
  const [showMapSearch, setShowMapSearch] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [mapCenter, setMapCenter] = useState([55.7558, 37.6173]);
  const [mapZoom, setMapZoom] = useState(3);

  // Auto-centre the map around the actual panorama location (~500 km radius)
  // so players don't have to scroll from world view every time.
  // Locations are not secret per game rules.
  const __mapCenteredRef = useRef(false);
  useEffect(() => {
    if (!panorama || panorama.latitude == null || panorama.longitude == null) return;
    if (__mapCenteredRef.current) return;
    if (result && result.has_answer) return; // don't override result view
    const center = [Number(panorama.latitude), Number(panorama.longitude)];
    const zoom = 6; // ~700 km wide at mid-latitudes
    setMapCenter(center);
    setMapZoom(zoom);
    if (mapRef.current) {
      try { mapRef.current.setView(center, zoom, { animate: false }); } catch (e) {}
    }
    __mapCenteredRef.current = true;
  }, [panorama, result]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasClickedMap, setHasClickedMap] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [allPanoramas, setAllPanoramas] = useState([]);
  const [myResults, setMyResults] = useState({ answers: [] });
  const [activationStatus, setActivationStatus] = useState(null); // Paid game activation status
  const [gameData, setGameData] = useState(null); // Game info with registration_deadline
  const [gameCountdown, setGameCountdown] = useState(null); // Countdown to game start
  const [hotspots, setHotspots] = useState([]); // Interactive zones on panorama
  const [foundHotspotIds, setFoundHotspotIds] = useState([]); // IDs of hotspots user has found
  const [allGameHotspots, setAllGameHotspots] = useState([]); // All hotspots in the game
  const [allFoundHotspotIds, setAllFoundHotspotIds] = useState([]); // All found hotspots in the game
  const [showHotspotsList, setShowHotspotsList] = useState(true);
  const [hintDialog, setHintDialog] = useState({ open: false, hotspot: null });
  const [highlightedHotspotId, setHighlightedHotspotId] = useState(null);
  const [userPointsBalance, setUserPointsBalance] = useState(0); // Show list of hotspots to find
  const [hotspotMessage, setHotspotMessage] = useState(null); // Message when hotspot is found
  const [userPoints, setUserPoints] = useState(0); // User's total points
  const [flyingStars, setFlyingStars] = useState([]); // Animation for flying stars
  const [showDropdown, setShowDropdown] = useState(false); // User menu dropdown
  const [hintStatus, setHintStatus] = useState(null); // {has_polygon, status, polygon}
  const [hintPolygonVisible, setHintPolygonVisible] = useState(true);
  const [hintMenuOpen, setHintMenuOpen] = useState(false);
  const [hintLoading, setHintLoading] = useState(false);
  const [hintTimerOpen, setHintTimerOpen] = useState(false);
  const [hintTimerSeconds, setHintTimerSeconds] = useState(60);
  const dropdownRef = useRef(null);
  const pointsBadgeRef = useRef(null); // Reference to points badge for animation target
  const lastMousePos = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 }); // Track mouse position
  
  const viewerRef = useRef(null);
  const mapRef = useRef(null);
  const panoramaMouseDownPos = useRef(null);
  const panoramaHasMoved = useRef(false);
  const mapMouseDownPos = useRef(null);
  const mapHasMoved = useRef(false);

  const hasPersistentAnswer = !!result && typeof result.guess_lat === 'number' && typeof result.guess_lng === 'number';

  // Track mouse/touch position for star animation
  useEffect(() => {
    const handleMouseMove = (e) => {
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    };
    const handleTouchMove = (e) => {
      if (e.touches && e.touches[0]) {
        lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };
    const handleTouchStart = (e) => {
      if (e.touches && e.touches[0]) {
        lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchstart', handleTouchStart);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchstart', handleTouchStart);
    };
  }, []);

  // Fetch user points
  useEffect(() => {
    const fetchUserPoints = async () => {
      if (!user) {
        setUserPoints(0);
        return;
      }
      try {
        const resp = await axios.get(`${API}/auth/me`, { headers: getAuthHeader() });
        setUserPoints(resp.data.total_points || 0);
      } catch (error) {
        console.error('Error fetching user points:', error);
      }
    };
    fetchUserPoints();
  }, [user, getAuthHeader]);


  useEffect(() => {
    fetchPanorama();
  }, [panoramaId, user?.id]);

  // Handle return from WordPress with hint signature
  useEffect(() => {
    const hintSig = searchParams.get('hint_sig');
    const hintPanorama = searchParams.get('hint_panorama');

    if (hintSig && hintPanorama && user) {
      // Force map mode
      setMapExpanded(true);
      (async () => {
        try {
          const resp = await axios.post(
            `${API}/hints/unlock?panorama_id=${encodeURIComponent(hintPanorama)}&sig=${encodeURIComponent(hintSig)}`,
            {},
            { headers: getAuthHeader() }
          );
          if (resp.data.unlocked) {
            toast.success(t('guessGame.hintActivated'));
            const hintResp = await axios.get(`${API}/hints/status/${hintPanorama}`, { headers: getAuthHeader() });
            setHintStatus(hintResp.data);
            // Zoom to polygon after map fully renders
            if (hintResp.data.polygon && hintResp.data.polygon.length > 0) {
              const bounds = L.latLngBounds(hintResp.data.polygon.map(p => [p.lat, p.lng]));
              // Multiple attempts to ensure map is ready
              [800, 1500, 2500].forEach(delay => {
                setTimeout(() => {
                  if (mapRef.current) {
                    mapRef.current.invalidateSize();
                    mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 18, animate: true });
                  }
                }, delay);
              });
            }
          }
        } catch (e) {
          console.warn('Hint unlock failed:', e);
        }
        // Clean URL params
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('hint_sig');
        newParams.delete('hint_panorama');
        newParams.delete('map');
        window.history.replaceState({}, '', window.location.pathname + (newParams.toString() ? '?' + newParams.toString() : ''));
      })();
    }
    // Clean map param
    if (searchParams.get('map')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('map');
      window.history.replaceState({}, '', window.location.pathname + (newParams.toString() ? '?' + newParams.toString() : ''));
    }
  }, [searchParams, user]);

  // Auto-fit polygon when map opens and hint is already unlocked
  useEffect(() => {
    if (mapExpanded && hintStatus && hintStatus.status === 'success' && hintStatus.polygon && hintStatus.polygon.length > 0) {
      const bounds = L.latLngBounds(hintStatus.polygon.map(p => [p.lat, p.lng]));
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.invalidateSize();
          mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 18, animate: true });
        }
      }, 600);
    }
  }, [mapExpanded]);

  // Reset state when panoramaId or user changes
  useEffect(() => {
    // Reset guess state for new panorama or new user
    setGuessMarker(null);
    setResult(null);
    // Don't close map if returning from hint quiz
    const keepMap = new URLSearchParams(window.location.search);
    if (!keepMap.get('hint_sig') && !keepMap.get('map') && !sessionStorage.getItem('hint_open_map')) {
      setMapExpanded(false);
    }
    setHasClickedMap(false);
    setIframeLoading(true);
    setFoundHotspotIds([]); // Clear found hotspots when user changes
    
    // Re-fetch game data to update myResults
    const refetchMyResults = async () => {
      if (!gameId || !user) return;
      try {
        const resultsResp = await axios.get(`${API}/games/${gameId}/my-results`, {
          headers: getAuthHeader()
        });
        setMyResults(resultsResp.data || { answers: [] });
      } catch (error) {
        console.error('Error refetching results:', error);
      }
    };
    refetchMyResults();
  }, [panoramaId, user?.id]);

  // Fetch all panoramas and user results for "Next" button logic
  useEffect(() => {
    const fetchGameData = async () => {
      if (!gameId) return;
      
      try {
        // Fetch all panoramas for this game
        const panoramasResp = await axios.get(`${API}/games/${gameId}/panoramas`);
        setAllPanoramas(panoramasResp.data || []);
        
        // Fetch all hotspots for the game (for progress display) - no auth needed
        try {
          const hotspotsPromises = (panoramasResp.data || []).map(p => 
            axios.get(`${API}/panoramas/${p.id}/hotspots`).catch(() => ({ data: [] }))
          );
          const hotspotsResults = await Promise.all(hotspotsPromises);
          const allHs = hotspotsResults.flatMap(r => r.data || []);
          setAllGameHotspots(allHs);
          
          // Fetch found hotspots for all panoramas (only if logged in)
          if (user) {
            const foundPromises = (panoramasResp.data || []).map(p =>
              axios.get(`${API}/panoramas/${p.id}/hotspots/found?_t=${Date.now()}`, { headers: getAuthHeader() })
                .catch(() => ({ data: { found_hotspot_ids: [] } }))
            );
            const foundResults = await Promise.all(foundPromises);
            const allFoundIds = foundResults.flatMap(r => r.data?.found_hotspot_ids || []);
            setAllFoundHotspotIds(allFoundIds);
          }
        } catch (e) {
        }
        
        // Fetch user's results (only if logged in)
        if (user) {
          const resultsResp = await axios.get(`${API}/games/${gameId}/my-results`, {
            headers: getAuthHeader()
          });
          setMyResults(resultsResp.data || { answers: [] });
        }
      } catch (error) {
        console.error('Error fetching game data:', error);
      }
    };
    
    fetchGameData();
  }, [gameId, user]);

  // Fetch game activation status for paid games
  useEffect(() => {
    const fetchActivationStatus = async () => {
      if (!gameId) return;
      
      try {
        const headers = user ? getAuthHeader() : {};
        const resp = await axios.get(`${API}/games/${gameId}/activation-status`, { headers });
        setActivationStatus(resp.data);
      } catch (error) {
        console.error('Error fetching activation status:', error);
        // Default to allowing play if check fails
        setActivationStatus({ requires_points: false, activated: true });
      }
    };
    
    fetchActivationStatus();
  }, [gameId, user]);

  // Fetch game data for countdown
  useEffect(() => {
    const fetchGameData = async () => {
      if (!gameId) return;
      try {
        const resp = await axios.get(`${API}/games/${gameId}`);
        setGameData(resp.data);
        
        // Set video URL if available - always try HLS first
        if (resp.data.video_path) {
          // Try HLS first, fallback handled by VideoContext
          setGameVideoUrl(`${window.location.origin}${API}/games/${gameId}/video/hls/master.m3u8`);
        }
        
        // Fetch timecodes
        const tcResp = await axios.get(`${API}/games/${gameId}/video/timecodes`);
        if (tcResp.data) {
          setGameTimecodes(tcResp.data);
        }
      } catch (error) {
        console.error('Error fetching game data:', error);
      }
    };
    fetchGameData();
  }, [gameId]);

  // Auto-open hotspots list for objects_only game mode
  useEffect(() => {
    if (gameData?.game_mode === "objects_only" || gameData?.game_mode === "location_objects") {
      setShowHotspotsList(true);
    }
  }, [gameData?.game_mode]);

  // Game countdown timer
  useEffect(() => {
    if (!gameData?.registration_deadline) {
      setGameCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const deadline = new Date(gameData.registration_deadline);
      const now = new Date();
      const diff = deadline - now;

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setGameCountdown({ days, hours, minutes, seconds, expired: false, deadline: gameData.registration_deadline });
      } else {
        setGameCountdown({ expired: true });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [gameData?.registration_deadline]);

  // Calculate showOriginalPoint early for use in useEffect
  const viewUserId = searchParams.get('viewUserId');
  const isViewingOtherUser = viewUserId && viewUserId !== user?.id;
  const showOriginalPointEarly = isViewingOtherUser 
    ? winners.length === 3
    : ((user && hasRole(['admin', 'editor'])) || winners.length === 3);

  // Auto-hide message after 2 seconds when map is expanded and original point is hidden
  useEffect(() => {
    if (mapExpanded && result && !showOriginalPointEarly) {
      setShowMessage(true); // Reset to visible when conditions change
      const timer = setTimeout(() => {
        setShowMessage(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [mapExpanded, result, showOriginalPointEarly]);

  // Load panorama when data is available
  useEffect(() => {
    if (panorama && panorama.html_file) {
      // Retry logic to ensure viewerRef is mounted
      const attemptLoad = (attempt = 0) => {
        if (viewerRef.current) {
          loadPanorama(panorama);
        } else if (attempt < 5) {
          setTimeout(() => attemptLoad(attempt + 1), 100 * (attempt + 1));
        } else {
          console.error('Failed to load panorama - viewerRef not mounted');
          setIframeLoading(false);
          toast.error(t('toast.loadError'));
        }
      };
      
      attemptLoad();
    }
  }, [panorama]);

  // Find next unanswered panorama
  const findNextUnansweredPanorama = () => {
    if (!allPanoramas.length || !user) return null;
    
    const answeredIds = new Set(myResults.answers.map(a => a.panorama_id));
    const currentIndex = allPanoramas.findIndex(p => p.id === panoramaId);
    
    // Search from current position to end
    for (let i = currentIndex + 1; i < allPanoramas.length; i++) {
      if (!answeredIds.has(allPanoramas[i].id)) {
        return allPanoramas[i].id;
      }
    }
    
    // Search from beginning to current position (circular)
    for (let i = 0; i < currentIndex; i++) {
      if (!answeredIds.has(allPanoramas[i].id)) {
        return allPanoramas[i].id;
      }
    }
    
    return null; // All answered
  };

  const fetchPanorama = async () => {
    try {
      setFoundHotspotIds([]); // Clear previous hotspots
      setLoading(true);
      const response = await axios.get(`${API}/panoramas/${panoramaId}`);
      const pano = response.data;
      
      if (!pano.latitude || !pano.longitude) {
        toast.error(t("errors.noCoordinates"));
        navigate(gameId ? `/games/${gameId}` : "/");
        return;
      }
      setPanorama(pano);
      
      // Load hint status
      if (user) {
        try {
          const hintResp = await axios.get(`${API}/hints/status/${panoramaId}`, { headers: getAuthHeader() });
          setHintStatus(hintResp.data);
        } catch (e) {
          setHintStatus(null);
        }
      }

      // Load winners if game_id exists
      if (pano.game_id) {
        try {
          const winnersResp = await axios.get(`${API}/games/${pano.game_id}/winners`);
          setWinners(winnersResp.data || []);
        } catch (error) {
          setWinners([]);
        }
        

      }
      
      // Attempt to load answer (logged in user or viewing another user's result)
      const viewUserId = searchParams.get('viewUserId');
      
      if (viewUserId) {
        // Viewing another user's result (no auth required)
        try {
          const gameResultsResp = await axios.get(`${API}/games/${pano.game_id}/results`);
          const userResult = gameResultsResp.data.find(r => r.user_id === viewUserId);
          
          let ans;
          if (userResult && userResult.answers) {
            const userAnswer = userResult.answers.find(a => a.panorama_id === pano.id);
            if (userAnswer) {
              ans = {
                has_answer: true,
                points: userAnswer.score,
                distance: userAnswer.distance,
                guess_lat: userAnswer.guess_lat,
                guess_lng: userAnswer.guess_lng,
                actual_lat: pano.latitude,
                actual_lng: pano.longitude,
                guessed_at: userAnswer.guessed_at
              };
            } else {
              ans = { has_answer: false };
            }
          } else {
            ans = { has_answer: false };
          }
          if (ans && ans.has_answer) {
            const normalized = {
              ...ans,
              guess_lat: ans?.guess_lat != null ? Number(ans.guess_lat) : undefined,
              guess_lng: ans?.guess_lng != null ? Number(ans.guess_lng) : undefined,
              actual_lat: pano?.latitude != null ? Number(pano.latitude) : undefined,
              actual_lng: pano?.longitude != null ? Number(pano.longitude) : undefined,
            };
            setResult(normalized);
            if (Number.isFinite(normalized.guess_lat) && Number.isFinite(normalized.guess_lng)) {
              setGuessMarker({ lat: normalized.guess_lat, lng: normalized.guess_lng });
            }
            // Delay map expansion to ensure DOM is ready
            setTimeout(() => {
              setMapExpanded(true);
            }, 150);
          }
        } catch (e) {
          console.error('Error loading viewUserId result:', e);
        }
      } else if (user) {
        // Load current user's answer
        try {
          const ansResp = await axios.get(`${API}/panoramas/${pano.id}/my-answer`, {
            headers: getAuthHeader()
          });
          let ans = ansResp.data;
          
          if (!ans || !ans.has_answer) {
            ans = await fetchAnswerFallback(pano, user);
          }
          
          if (ans && ans.has_answer) {
            const normalized = {
              ...ans,
              guess_lat: ans?.guess_lat != null ? Number(ans.guess_lat) : undefined,
              guess_lng: ans?.guess_lng != null ? Number(ans.guess_lng) : undefined,
              actual_lat: pano?.latitude != null ? Number(pano.latitude) : undefined,
              actual_lng: pano?.longitude != null ? Number(pano.longitude) : undefined,
            };
            setResult(normalized);
            if (Number.isFinite(normalized.guess_lat) && Number.isFinite(normalized.guess_lng)) {
              setGuessMarker({ lat: normalized.guess_lat, lng: normalized.guess_lng });
            }
            // Delay map expansion to ensure DOM is ready
            setTimeout(() => {
              setMapExpanded(true);
            }, 150);
            // force fit after expand
            setTimeout(() => {
              if (mapRef.current && Number.isFinite(normalized.guess_lat)) {
                try {
                  const bounds = L.latLngBounds([
                    [normalized.guess_lat, normalized.guess_lng],
                    [normalized.actual_lat, normalized.actual_lng],
                  ]);
                  mapRef.current.fitBounds(bounds, { padding: [60, 60] });
                } catch (e) {}
              }
            }, 320);
          }
        } catch (e) {
          console.error('Error loading user answer:', e);
          // fallback if 404
          const ans = await fetchAnswerFallback(pano, user);
          if (ans && ans.has_answer) {
            const normalized = {
              ...ans,
              guess_lat: ans?.guess_lat != null ? Number(ans.guess_lat) : undefined,
              guess_lng: ans?.guess_lng != null ? Number(ans.guess_lng) : undefined,
              actual_lat: pano?.latitude != null ? Number(pano.latitude) : undefined,
              actual_lng: pano?.longitude != null ? Number(pano.longitude) : undefined,
            };
            setResult(normalized);
            if (Number.isFinite(normalized.guess_lat) && Number.isFinite(normalized.guess_lng)) {
              setGuessMarker({ lat: normalized.guess_lat, lng: normalized.guess_lng });
            }
            // Delay map expansion to ensure DOM is ready
            setTimeout(() => {
              setMapExpanded(true);
            }, 150);
            setTimeout(() => {
              if (mapRef.current && Number.isFinite(normalized.guess_lat)) {
                try {
                  const bounds = L.latLngBounds([
                    [normalized.guess_lat, normalized.guess_lng],
                    [normalized.actual_lat, normalized.actual_lng],
                  ]);
                  mapRef.current.fitBounds(bounds, { padding: [60, 60] });
                } catch (e2) {}
              }
            }, 320);
          }
        }
      }
      
      // Load hotspots for this panorama
      try {
        const hotspotsResp = await axios.get(`${API}/panoramas/${pano.id}/hotspots`);
        setHotspots(hotspotsResp.data || []);
        
        // Load user's points balance for hints
        if (user && !searchParams.get('viewUserId')) {
          try {
            const balanceResp = await axios.get(`${API}/users/me/points-balance`, { headers: getAuthHeader() });
            setUserPointsBalance(balanceResp.data.total_points || 0);
          } catch (e) {
            console.error('Error loading points balance:', e);
          }
        }
        
        // Load found hotspots - for viewUserId use that user's hotspots, otherwise current user
        const viewUserId = searchParams.get('viewUserId');
        if (viewUserId) {
          // Viewing another user's results - load their found hotspots
          try {
            const foundResp = await axios.get(
              `${API}/panoramas/${pano.id}/hotspots/found/${viewUserId}?_t=${Date.now()}`
            );
            setFoundHotspotIds(foundResp.data.found_hotspot_ids || []);
          } catch (e) {
            setFoundHotspotIds([]); // Clear on error
          }
        } else if (user) {
          try {
            const foundResp = await axios.get(
              `${API}/panoramas/${pano.id}/hotspots/found?_t=${Date.now()}`,
              { headers: getAuthHeader() }
            );
            setFoundHotspotIds(foundResp.data.found_hotspot_ids || []);
          } catch (e) {
            setFoundHotspotIds([]); // Clear on error
          }
        } else {
          setFoundHotspotIds([]); // Clear if no user
        }
      } catch (e) {
      }
    } catch (error) {
      console.error("Error fetching panorama:", error);
      toast.error(t("toast.loadError"));
      navigate(gameId ? `/games/${gameId}` : "/");
    } finally {
      setLoading(false);
    }
  };

  const loadPanorama = (pano) => {
    if (!viewerRef.current) {
      console.error('viewerRef.current is null');
      setIframeLoading(false);
      return;
    }
    
    // Clear previous content
    viewerRef.current.innerHTML = '';
    
    if (!pano.html_file) {
      console.error('No html_file in panorama data:', pano);
      setIframeLoading(false);
      toast.error(t('errors.noPanoramaFile'));
      return;
    }
    
    setIframeLoading(true);
    
    const iframe = document.createElement("iframe");
    iframe.src = `${API}/panoramas/${pano.id}/files/${pano.html_file}`;
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.border = "none";
    iframe.setAttribute("allowfullscreen", "true");
    iframe.setAttribute("allow", "accelerometer; gyroscope");
    
    // Hide loading indicator when iframe loads
    iframe.onload = () => {
      setTimeout(() => setIframeLoading(false), 300);
    };
    
    iframe.onerror = () => {
      console.error('Failed to load panorama iframe');
      setIframeLoading(false);
      toast.error(t('toast.loadError'));
    };
    
    viewerRef.current.appendChild(iframe);
    // Store iframe reference for postMessage
    viewerRef.current.iframeRef = iframe;
  };

  // Buy hint and reveal hotspot location
  const handleBuyHint = useCallback(async (hotspot) => {
    if (!user) {
      toast.info(t('toast.loginRequired') || 'Войдите, чтобы использовать подсказку');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/hotspots/${hotspot.id}/buy-hint`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.success) {
        setUserPointsBalance(response.data.new_balance);
        // Update balance in PageHeader with animation
        console.log("[GuessGame] Calling updatePointsWithAnimation:", response.data.new_balance);
        if (window.updatePointsWithAnimation) {
          window.updatePointsWithAnimation(response.data.new_balance);
        } else {
          console.error("[GuessGame] updatePointsWithAnimation not found!");
        }
        setHintDialog({ open: false, hotspot: null });
        setHighlightedHotspotId(hotspot.id);
        
        toast.success(
          t('toast.hintPurchased') || `Подсказка куплена! Списано ${response.data.points_spent} баллов`,
          { duration: 3000 }
        );
        
        // Center panorama on the hotspot and highlight it via postMessage to iframe
        const hs = response.data.hotspot;
        
        // Find iframe - try multiple methods
        let iframe = viewerRef.current?.iframeRef;
        if (!iframe && viewerRef.current) {
          iframe = viewerRef.current.querySelector("iframe");
        }
        if (!iframe) {
          iframe = document.querySelector("iframe[src*=panorama]") || 
                   document.querySelector("iframe[src*=tour]");
        }
        
        console.log("[Hint] Looking for iframe:", { 
          hasViewerRef: !!viewerRef.current,
          hasIframeRef: !!viewerRef.current?.iframeRef,
          foundIframe: !!iframe,
          hotspotId: hs?.id,
          center_ath: hs?.center_ath,
          center_atv: hs?.center_atv
        });
        
        if (hs && iframe && iframe.contentWindow) {
          const message = {
            type: "highlightHotspot",
            hotspotId: hs.id,
            ath: hs.center_ath,
            atv: hs.center_atv,
            points: hs.points
          };
          console.log("[Hint] Sending postMessage:", message);
          iframe.contentWindow.postMessage(message, "*");
          
          // Remove highlight after 8 seconds
          setTimeout(() => {
            setHighlightedHotspotId(null);
          }, 8000);
        } else {
          console.error("[Hint] Cannot send message - iframe not found");
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Ошибка при покупке подсказки';
      toast.error(errorMsg);
      setHintDialog({ open: false, hotspot: null });
    }
  }, [user, getAuthHeader, t]);

  // Handle "Get Hint" button click - request token and redirect to WordPress
  const handleBuyHintWithPoints = async () => {
    if (!user || !panorama) return;
    setHintLoading(true);
    setHintMenuOpen(false);
    try {
      const resp = await axios.post(`${API}/hints/buy/${panorama.id}`, {}, { headers: getAuthHeader() });
      if (resp.data.success) {
        toast.success(`Подсказка куплена! Списано ${resp.data.points_spent} баллов`);
        if (window.updatePointsWithAnimation) {
          window.updatePointsWithAnimation(resp.data.new_balance);
        }
        // Auto-unlock polygon
        const hintResp = await axios.get(`${API}/hints/status/${panorama.id}`, { headers: getAuthHeader() });
        setHintStatus(hintResp.data);
        if (hintResp.data.polygon && hintResp.data.polygon.length > 0) {
          const bounds = L.latLngBounds(hintResp.data.polygon.map(p => [p.lat, p.lng]));
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.invalidateSize();
              mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 18, animate: true });
            }
          }, 600);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка покупки подсказки');
    } finally {
      setHintLoading(false);
    }
  };

  const handleRequestHint = async () => {
    if (!user || !panorama) return;
    setHintLoading(true);
    try {
      const fp = await getDeviceFingerprint();
      const resp = await axios.post(`${API}/hints/request-token`, {
        panorama_id: panorama.id,
        game_id: panorama.game_id || gameId,
        fingerprint: fp
      }, { headers: getAuthHeader() });
      
      if (resp.data.redirect_url) {
        window.location.href = resp.data.redirect_url;
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Ошибка запроса подсказки';
      toast.error(detail);
      setHintLoading(false);
    }
  };

  // Handle hotspot click (used by postMessage listener and directly)
  const handleHotspotClick = useCallback(async (hotspot) => {
    // Block hotspot interaction when viewing another user's results
    if (isViewingOtherUser) {
      return;
    }
    
    if (!user) {
      toast.info(t('toast.loginToGetPoints'));
      return;
    }
    
    if (foundHotspotIds.includes(hotspot.id)) {
      setHotspotMessage({
        name: getTranslatedText(hotspot.name_translations, language, hotspot.name),
        points: 0,
        alreadyFound: true
      });
      setTimeout(() => setHotspotMessage(null), 3000);
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/panoramas/${panorama?.id}/hotspots/${hotspot.id}/found`,
        {},
        { headers: getAuthHeader() }
      );
      
      setFoundHotspotIds(prev => [...prev, hotspot.id]);
      setAllFoundHotspotIds(prev => {
        // Avoid duplicates
        if (prev.includes(hotspot.id)) {
          return prev;
        }
        const newArray = [...prev, hotspot.id];
        return newArray;
      });
      setHotspotMessage({
        name: getTranslatedText(response.data.hotspot_name_translations, language, response.data.hotspot_name),
        points: response.data.points_awarded,
        alreadyFound: response.data.already_found
      });
      
      // Trigger flying stars animation if points awarded
      if (response.data.points_awarded > 0 && !response.data.already_found) {
        triggerFlyingStars(response.data.points_awarded);
        // Update user points after animation
        setTimeout(() => {
          setUserPoints(prev => prev + response.data.points_awarded);
        }, 800);
      }
      
      setTimeout(() => setHotspotMessage(null), 4000);
    } catch (error) {
      console.error('Error marking hotspot found:', error);
      toast.error(t('common.error'));
    }
  }, [user, foundHotspotIds, panorama?.id, getAuthHeader, isViewingOtherUser]);

  // Function to trigger flying stars animation
  const triggerFlyingStars = (points) => {
    const numStars = Math.min(points, 10); // Max 10 stars
    const newStars = [];
    
    // Get click position from mouse tracker, with fallback to panorama center
    let clickX = lastMousePos.current.x;
    let clickY = lastMousePos.current.y;
    
    // Fallback: if coordinates are at default position (center), use panorama viewer center
    const isDefaultPos = clickX === window.innerWidth / 2 && clickY === window.innerHeight / 2;
    if (isDefaultPos && viewerRef.current) {
      const rect = viewerRef.current.getBoundingClientRect();
      clickX = rect.left + rect.width / 2;
      clickY = rect.top + rect.height / 2;
    }
    
    for (let i = 0; i < numStars; i++) {
      newStars.push({
        id: Date.now() + i,
        startX: clickX + (Math.random() - 0.5) * 60,
        startY: clickY + (Math.random() - 0.5) * 60,
        delay: i * 40
      });
    }
    
    setFlyingStars(newStars);
    
    // Clear stars after animation
    setTimeout(() => {
      setFlyingStars([]);
    }, 1500);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Listen for hotspot clicks from KRPano iframe
  useEffect(() => {
    const handleMessage = async (event) => {
      // Handle hint polygon click - award points for the hinted object
      if (event.data && event.data.type === 'hint_polygon_clicked') {
        const hotspot_id = event.data.hotspotId;
        const hs = hotspots.find(h => h.id === hotspot_id);
        if (hs) {
          handleHotspotClick(hs);
        } else if (hotspot_id) {
          handleHotspotClick({ id: hotspot_id });
        }
        return;
      }
      
      // Support both message types: 'hotspot_click' (from production) and 'hotspot_clicked'
      if (event.data && (event.data.type === 'hotspot_click' || event.data.type === 'hotspot_clicked')) {
        const hotspotData = event.data.hotspot || event.data;
        const hotspot_id = hotspotData.id || hotspotData.hotspot_id;
        const hotspot_name = hotspotData.name || hotspotData.hotspot_name;
        const reward_points = hotspotData.reward_points;
        
        // Update mouse position from screen coordinates sent by KRPano
        const clickX = event.data.clickX;
        const clickY = event.data.clickY;
        if (clickX > 0 && clickY > 0) {
          // Coordinates are already screen coordinates (clientX/clientY from touch/mouse event)
          lastMousePos.current = {
            x: clickX,
            y: clickY
          };
        } else {
          // Fallback: use center of panorama viewer
          if (viewerRef.current) {
            const rect = viewerRef.current.getBoundingClientRect();
            lastMousePos.current = {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2
            };
          }
        }
        
        // Find the hotspot in our list and trigger click handler
        const hs = hotspots.find(h => h.id === hotspot_id);
        if (hs) {
          handleHotspotClick(hs);
        } else if (hotspot_id) {
          // Hotspot not in list, but we still have the data from message
          handleHotspotClick({ id: hotspot_id, name: hotspot_name, reward_points });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [hotspots, foundHotspotIds, user, panorama, handleHotspotClick]);

  const handleMapClick = (latlng) => {
    // Only set marker when map is expanded, no result yet, and not transitioning
    setHintMenuOpen(false);
    if (!result && mapExpanded && !isTransitioning) {
      setGuessMarker({ lat: latlng.lat, lng: latlng.lng });
      setHasClickedMap(true); // Mark that user has clicked on map
    }
  };

  const handleGuess = async () => {
    if (!guessMarker) {
      toast.error(t("toast.selectLocation"));
      return;
    }

    // Check if user is blocked
    if (user?.blocked) {
      toast.error(t("errors.accountBlocked"));
      return;
    }

    try {
      const fp = await getDeviceFingerprint();
      const response = await axios.post(
        `${API}/panoramas/${panoramaId}/guess`,
        { ...guessMarker, device_fingerprint: fp },
        { headers: getAuthHeader() }
      );
      setResult(response.data);
      setMapExpanded(true);
      
      // Update myResults to include this new answer with complete response data
      setMyResults(prev => ({
        ...prev,
        answers: [...(prev.answers || []), {
          panorama_id: panoramaId,
          score: response.data.points,
          distance: response.data.distance,
          guess_lat: response.data.guess_lat,
          guess_lng: response.data.guess_lng,
          guessed_at: new Date().toISOString()
        }]
      }));

      // Fit bounds to show both points nicely
      setTimeout(() => {
        try {
          const bounds = L.latLngBounds([
            [response.data.guess_lat, response.data.guess_lng],
            [response.data.actual_lat, response.data.actual_lng],
          ]);
          if (mapRef.current) {
            mapRef.current.fitBounds(bounds, { padding: [60, 60] });
          }
        } catch (e) {}
      }, 150);
      
      // Show result toast with points
      const distance = response.data.distance;
      const points = response.data.points;
      let message = "";
      if (distance < 100) {
        message = t("guessGame.excellent") + " ";
      } else if (distance < 1000) {
        message = t("guessGame.great") + " ";
      } else if (distance < 10000) {
        message = t("guessGame.good") + " ";
      } else {
        message = "";
      }
      
      // toast removed - result shown in floating display
      
    } catch (error) {
      console.error("Error submitting guess:", error);
      const errorMsg = error.response?.data?.detail || t("guessGame.sendResultError");
      
      // Check if error is about email verification
      if (error.response?.status === 403 && errorMsg.includes("verify your email")) {
        setShowVerificationModal(true);
      } else {
        toast.error(errorMsg);
        
        // If blocked, redirect to home after showing error
        if (error.response?.status === 403) {
          setTimeout(() => navigate(gameId ? `/games/${gameId}` : '/'), 2000);
        }
      }
    }
  };

  // Panorama handlers (when panorama is minimized)
  const handlePanoramaMouseDown = (e) => {
    if (!mapExpanded) return; // Only handle when panorama is minimized
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    panoramaMouseDownPos.current = { x: clientX, y: clientY };
    panoramaHasMoved.current = false;
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { headers: getAuthHeader() });
      toast.success(t("auth.emailSent"));
      setShowVerificationModal(false);
    } catch (error) {
      console.error("Error resending verification:", error);
      toast.error(error.response?.data?.detail || t("toast.sendError"));
    } finally {
      setResendingEmail(false);
    }
  };

  const handlePanoramaMouseMove = (e) => {
    if (!mapExpanded || !panoramaMouseDownPos.current) return;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    // When expanding from preview, explicitly disable dragging during the first click-drag frame
    if (mapRef.current && !mapExpanded) {
      try { mapRef.current.dragging && mapRef.current.dragging.disable(); } catch(e) {}
      setTimeout(() => {
        try { mapRef.current.dragging && mapRef.current.dragging.enable(); } catch(e) {}
      }, 250);
    }

    const dx = Math.abs(clientX - panoramaMouseDownPos.current.x);
    const dy = Math.abs(clientY - panoramaMouseDownPos.current.y);
    
    // If moved more than 5 pixels, consider it as dragging
    if (dx > 5 || dy > 5) {
      panoramaHasMoved.current = true;
    }
  };

  const handlePanoramaMouseUp = (e) => {
    if (!mapExpanded || !panoramaMouseDownPos.current) return;
    
    // Only toggle if there was no significant movement
    if (!panoramaHasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      toggleMapSize(e);
    }
    
    panoramaMouseDownPos.current = null;
    panoramaHasMoved.current = false;
  };

  // Map handlers (when map is minimized)
  const handleMapMouseDown = (e) => {
    if (mapExpanded) return; // Only handle when map is minimized
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    mapMouseDownPos.current = { x: clientX, y: clientY };
    mapHasMoved.current = false;
  };

  const handleMapMouseMove = (e) => {
    if (mapExpanded || !mapMouseDownPos.current) return;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const dx = Math.abs(clientX - mapMouseDownPos.current.x);
    const dy = Math.abs(clientY - mapMouseDownPos.current.y);
    
    // If moved more than 5 pixels, consider it as dragging
    if (dx > 5 || dy > 5) {
      mapHasMoved.current = true;
    }
  };

  const handleMapMouseUp = (e) => {
    if (mapExpanded || !mapMouseDownPos.current) return;
    
    // Only toggle if there was no significant movement
    if (!mapHasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      toggleMapSize(e);
    }
    
    mapMouseDownPos.current = null;
    mapHasMoved.current = false;
  };

  const handleMapMove = (center, zoom) => {
    setMapCenter([center.lat, center.lng]);
    setMapZoom(zoom);
  };

  const toggleMapSize = (e) => {
    // Prevent toggle if clicking on map controls
    if (e && e.target.closest('.map-controls, .game-bottom-bar')) {
      return;
    }
    
    // Set transitioning flag to prevent clicks during transition
    setIsTransitioning(true);
    
    // Save current map state before toggling
    if (mapRef.current && mapExpanded) {
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      setMapCenter([center.lat, center.lng]);
      setMapZoom(zoom);
    }
    
    const newExpandedState = !mapExpanded;
    setMapExpanded(newExpandedState);
    
    // Force map to resize and restore position after state change
    if (newExpandedState) {
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.invalidateSize();
          mapRef.current.setView(mapCenter, mapZoom);
        }
        // Clear transitioning flag after animation completes
        setTimeout(() => setIsTransitioning(false), 100);
      }, 100);
    } else {
      // Clear transitioning flag for collapse
      setTimeout(() => setIsTransitioning(false), 400);
    }
  };

  const getTileLayerUrl = () => {
    switch (mapType) {
      case "satellite":
        return "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
      case "hybrid":
        return "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
      default:
        return "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
    }
  };

  if (loading) {
    return (
      <div className="game-container">
        <div className="loading-state">{t("guessGame.loading")}</div>
      </div>
    );
  }

  // Use the same showOriginalPoint calculation (already defined above in useEffect)
  const showOriginalPoint = showOriginalPointEarly;

  const __fsWrap = isFullscreen ? {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 99998,
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch'
  } : {};
  return (
    <div className="game-container" data-testid="guess-game" style={__fsWrap}>
      {/* Language Switcher for non-authenticated users */}
      {!user && (
        <div style={{
          position: 'fixed',
          top: '12px',
          right: '0.75rem',
          zIndex: 100000,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <a
            href="/game/login"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600',
              textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap'
            }}
          >
            {t("auth.login")}
          </a>
          <a
            href="/game/register"
            style={{
              background: 'white',
              color: '#667eea',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600',
              textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              border: '1px solid #667eea',
              whiteSpace: 'nowrap'
            }}
          >
            {t("auth.register")}
          </a>
          <LanguageSwitcher flagOnly style={{ flexShrink: 0 }} />
        </div>
      )}
      
      {/* User Points Badge & Avatar - Top Right */}
      {user && (
        <div style={{
          position: 'fixed',
          top: '12px',
          right: '0.75rem',
          zIndex: 100000,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div 
            ref={pointsBadgeRef}
            style={{
              background: userPoints > 0 
                ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
              color: userPoints > 0 ? '#92400e' : '#6b7280',
              padding: '6px 12px',
              height: '32px',
              boxSizing: 'border-box',
              borderRadius: '20px',
              fontSize: '14px',
              lineHeight: 1,
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              border: userPoints > 0 ? '1px solid #f59e0b' : '1px solid #d1d5db',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
          >
            <Star size={14} style={{ width: 14, height: 14, flexShrink: 0 }} /> {Math.round(userPoints)}
          </div>
          {/* Language Switcher - after points, before avatar */}
          <LanguageSwitcher flagOnly style={{ flexShrink: 0 }} />
          <div ref={dropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
              data-testid="avatar-menu-button"
              type="button"
              style={{
                cursor: 'pointer',
                background: 'white',
                borderRadius: '50%',
                padding: '2px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                border: 'none',
                outline: 'none'
              }}
              title={t("guessGame.userMenu")}
              aria-label={t("guessGame.userMenu")}
            >
              <Avatar
                userId={user.id}
                avatarUrl={user.avatar_url}
                fullImageUrl={user.full_image_url}
                userName={user.full_name}
                userEmail={user.email}
                size={36}
                clickable={false}
              />
            </button>
            
            {/* Dropdown Menu - FIXED position like PageHeader */}
            {showDropdown && (
              <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  top: '4rem',
                  right: '1rem',
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  minWidth: '200px',
                  maxWidth: 'calc(100vw - 2rem)',
                  overflow: 'hidden',
                  zIndex: 100001
                }}>
                {/* User Info */}
                <div style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #e5e7eb',
                  background: '#f9fafb'
                }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {user.full_name}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {user.email}
                  </div>
                </div>
                
                {/* Menu Items */}
                <div style={{ padding: '0.25rem 0' }}>
                  <div
                    onClick={() => { navigate('/profile'); setShowDropdown(false); }}
                    style={{
                      padding: '0.6rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <User size={16} />
                    <span>{t("guessGame.profile")}</span>
                  </div>
                  
                  {hasRole('admin') && (
                    <div
                      onClick={() => { navigate('/admin'); setShowDropdown(false); }}
                      style={{
                        padding: '0.6rem 1rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.9rem'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <Shield size={16} />
                      <span>{t("guessGame.adminPanel")}</span>
                    </div>
                  )}
                  
                  <div
                    onClick={() => { logout(); setShowDropdown(false); navigate('/'); }}
                    style={{
                      padding: '0.6rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.9rem',
                      color: '#ef4444',
                      borderTop: '1px solid #e5e7eb'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <LogOut size={16} />
                    <span>{t("guessGame.logout")}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Game Winners medal strip (under the avatar) */}
      {gameId && (
        <GameWinnersStrip
          gameId={gameId}
          currentUserId={user?.id}
          variant="bare"
          anchor={{ position: 'fixed', top: '58px', right: '0.75rem', zIndex: 9990 }}
        />
      )}

      {/* Flying Stars Animation */}
      {flyingStars.map(star => {
        // Get badge position using ref - target is the points badge
        let targetX = window.innerWidth - 60;
        let targetY = 16;
        if (pointsBadgeRef.current) {
          const rect = pointsBadgeRef.current.getBoundingClientRect();
          targetX = rect.left + rect.width / 2;
          targetY = rect.top + rect.height / 2;
        }
        
        const keyframeName = `fly-${star.id}`;
        
        return (
          <div key={star.id}>
            <style>{`
              @keyframes ${keyframeName} {
                0% {
                  opacity: 1;
                  transform: translate(${star.startX}px, ${star.startY}px) scale(1.3);
                }
                80% {
                  opacity: 1;
                }
                100% {
                  opacity: 0;
                  transform: translate(${targetX}px, ${targetY}px) scale(0.4);
                }
              }
            `}</style>
            <div
              style={{
                position: 'fixed',
                left: 0,
                top: 0,
                fontSize: '24px',
                zIndex: 10000,
                pointerEvents: 'none',
                animation: `${keyframeName} 0.7s ease-in ${star.delay}ms forwards`
              }}
            >
              ⭐
            </div>
          </div>
        );
      })}

      {/* Panorama Viewer */}
      <div
        className={`panorama-section ${mapExpanded ? "minimized" : ""}`}
        data-testid="panorama-section"
      >
        {iframeLoading && !mapExpanded && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            color: 'white',
            gap: '1rem'
          }}>
            <Loader2 size={48} className="animate-spin" />
            <div style={{ fontSize: "18px" }}>{t("guessGame.loadingPanorama")}</div>
          </div>
        )}
        {/* Hotspots list button and progress - top left */}
        {hotspots.length > 0 && !mapExpanded && (
          <div style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div
              onClick={() => setShowHotspotsList(!showHotspotsList)}
              style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: '1px solid #f59e0b',
                borderRadius: '20px',
                padding: '6px 12px',
                height: '32px',
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '600',
                fontSize: '14px',
                lineHeight: 1,
                color: '#92400e'
              }}
              title={t("guessGame.whatToFind")}
            >
              <Search size={14} style={{ width: 14, height: 14, flexShrink: 0 }} />
              <span>{hotspots.filter(h => !foundHotspotIds.includes(h.id)).length}</span>
            </div>
            
            {/* Progress badge */}
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #f59e0b',
              borderRadius: '20px',
              padding: '6px 12px',
              height: '32px',
              boxSizing: 'border-box',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '600',
              fontSize: '14px',
              lineHeight: 1,
              color: '#92400e'
            }}>
              <Target size={14} style={{ width: 14, height: 14, flexShrink: 0 }} />
              <span>{foundHotspotIds.length}/{hotspots.length}</span>
            </div>
            
            {/* Video mini player toggle badge - show if panorama has timecode */}
            {(videoActive || gameTimecodes.find(t => t.panorama_id === panoramaId)) && gameVideoUrl && (
              <div
                onClick={async () => {
                  const tc = gameTimecodes.find(t => t.panorama_id === panoramaId);
                  const seekTime = tc ? Math.max(0, tc.timecode - 5) : 0;
                  
                  if (!videoActive && startVideoAtTime && gameVideoUrl) {
                    // Start video at timecode - 5 sec
                    await startVideoAtTime(gameId, gameVideoUrl, seekTime);
                    setMiniHidden(false);
                  } else if (miniHidden) {
                    // Video active but hidden - just show, continue from current time
                    setMiniHidden(false);
                  } else {
                    // Toggle visibility (hide)
                    setMiniHidden(true);
                  }
                }}
                style={{
                  background: miniHidden 
                    ? 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  border: miniHidden ? '1px solid #9ca3af' : '1px solid #f59e0b',
                  borderRadius: '20px',
                  padding: '6px 12px',
                  height: '32px',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '600',
                  fontSize: '14px',
                  lineHeight: 1,
                  color: miniHidden ? '#6b7280' : '#92400e'
                }}
                title={miniHidden ? 'Показать видео' : 'Скрыть видео'}
              >
                {miniHidden 
                  ? <VideoOff size={14} style={{ width: 14, height: 14, flexShrink: 0 }} />
                  : <Video size={14} style={{ width: 14, height: 14, flexShrink: 0 }} />
                }
              </div>
            )}
            
            {showHotspotsList && (
              <div style={{
                position: 'fixed',
                top: '130px',
                left: '1rem',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                minWidth: '220px',
                maxWidth: 'calc(100vw - 2rem)',
                maxHeight: 'calc(100vh - 100px)',
                overflowY: 'auto',
                zIndex: 100002,
                WebkitOverflowScrolling: 'touch'
              }}>
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e5e7eb',
                  fontWeight: 600,
                  color: '#374151',
                  fontSize: '0.9rem'
                }}>
                  {t("guessGame.findOnPanorama")}
                </div>
                {hotspots.map((hs, idx) => {
                  const isFound = foundHotspotIds.includes(hs.id);
                  const isHighlighted = highlightedHotspotId === hs.id;
                  const viewUserId = searchParams.get('viewUserId');
                  const isViewingOther = viewUserId && viewUserId !== user?.id;
                  return (
                    <div
                      key={hs.id || idx}
                      style={{
                        padding: '10px 16px',
                        borderBottom: idx < hotspots.length - 1 ? '1px solid #f3f4f6' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        color: isFound ? '#22c55e' : isHighlighted ? '#f59e0b' : '#374151',
                        textDecoration: isFound ? 'line-through' : 'none',
                        opacity: isFound ? 0.7 : 1,
                        fontSize: '0.9rem',
                        backgroundColor: isHighlighted ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                        transition: 'background-color 0.3s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <span>{isFound ? '✓' : isHighlighted ? '👁' : '○'}</span>
                        <span>{getTranslatedText(hs.name_translations, language, hs.name) || `Объект ${idx + 1}`}</span>
                      </div>
                      {!isFound && !isViewingOther && user && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setHintDialog({ open: true, hotspot: hs });
                          }}
                          style={{
                            padding: '4px 8px',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            border: 'none',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap'
                          }}
                          title={t('guessGame.showObject100Points')}
                        >
                          <Eye size={12} />
                          <span>100</span>
                        </button>
                      )}
                    </div>
                  );
                })}
                {hotspots.length === 0 && (
                  <div style={{ padding: '16px', color: '#9ca3af', textAlign: 'center' }}>
                    {t("guessGame.noObjectsToFind")}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        <div 
          ref={viewerRef} 
          className="panorama-viewer-game"
          onTouchStart={(e) => {
            if (e.touches && e.touches[0]) {
              lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
          }}
          onTouchMove={(e) => {
            if (e.touches && e.touches[0]) {
              lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
          }}
          onClick={(e) => {
            lastMousePos.current = { x: e.clientX, y: e.clientY };
          }}
        ></div>
        
        {/* Hotspot found message */}
        {hotspotMessage && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 200,
            background: hotspotMessage.alreadyFound 
              ? 'rgba(107, 114, 128, 0.95)' 
              : 'rgba(16, 185, 129, 0.95)',
            color: 'white',
            padding: '20px 32px',
            borderRadius: '16px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            animation: 'fadeIn 0.3s ease'
          }}>
            <Gift size={32} style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '4px' }}>
              {hotspotMessage.alreadyFound ? t('guessGame.alreadyFound') : t('guessGame.youFound')}
            </div>
            <div style={{ fontSize: '1rem' }}>{hotspotMessage.name}</div>
          </div>
        )}
        
        {/* Allow toggling to map by clicking minimized panorama */}
        {mapExpanded && (
          <>
            {/* Hide iframe controls by overlaying static preview image to avoid UI clutter in mini mode */}
            {panorama?.preview_path && (
              <img
                className="panorama-mini-preview"
                src={`${API}/panoramas/${panorama.id}/preview`}
                alt={panorama.name}
              />
            )}
            <div
              className="panorama-click-overlay"
              onMouseDown={handlePanoramaMouseDown}
              onMouseMove={handlePanoramaMouseMove}
              onMouseUp={handlePanoramaMouseUp}
              onTouchStart={handlePanoramaMouseDown}
              onTouchMove={handlePanoramaMouseMove}
              onTouchEnd={handlePanoramaMouseUp}
              data-testid="panorama-click-overlay"
            ></div>
          </>
        )}

      </div>

      {/* Map Section - hide for objects_only mode (except for admins) */}
      {(gameData?.game_mode !== 'objects_only' || hasRole(['admin', 'editor'])) && (
      <div
        className={`map-section ${mapExpanded ? "expanded" : ""}`}
        onMouseDown={handleMapMouseDown}
        onMouseMove={handleMapMouseMove}
        onMouseUp={handleMapMouseUp}
        onTouchStart={handleMapMouseDown}
        onTouchMove={handleMapMouseMove}
        onTouchEnd={handleMapMouseUp}
        style={{ cursor: !mapExpanded ? 'pointer' : 'default' }}
        data-testid="map-section"
      >
        {/* Overlay to intercept clicks while minimized, preventing leaflet from grabbing drag */}
        {!mapExpanded && <div className="map-preview-overlay" onClick={toggleMapSize} />}

        {/* Map */}
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          className="leaflet-map"
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer
            key={mapType}
            url={getTileLayerUrl()}
            maxZoom={19}
          />
          
          {mapType === "hybrid" && (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              opacity={0.3}
            />
          )}

          <MapRefHandler mapRef={mapRef} onMapMove={handleMapMove} />
          <MapViewController mapExpanded={mapExpanded} result={result} showOriginalPoint={showOriginalPoint} savedCenter={mapCenter} savedZoom={mapZoom} />
          
          <MapClickHandler
            onMapClick={handleMapClick}
            disabled={!mapExpanded || !!result || isTransitioning || isViewingOtherUser}
          />

          {/* Disable map drag before first click to prevent accidental move on mousedown */}
          {mapExpanded && !hasPersistentAnswer && !guessMarker && (
            <MapDragBlocker active={mapExpanded && !hasPersistentAnswer && !guessMarker} />
          )}

          {guessMarker && (
            <Marker
              position={[guessMarker.lat, guessMarker.lng]}
              icon={guessIcon}
            />
          )}

          {result && Number.isFinite(+result.guess_lat) && Number.isFinite(+result.guess_lng) && Number.isFinite(+result.actual_lat) && Number.isFinite(+result.actual_lng) && (
            <>
              {/* Show original marker and line only if showOriginalPoint is true */}
              {showOriginalPoint ? (
                <>
                  <Marker
                    position={[+result.actual_lat, +result.actual_lng]}
                    icon={actualIcon}
                  />
                  <Polyline
                    positions={[
                      [+result.guess_lat, +result.guess_lng],
                      [+result.actual_lat, +result.actual_lng],
                    ]}
                    color="#0a4797"
                    weight={3}
                    dashArray="10, 10"
                  />
                </>
              ) : null}
            </>
          )}

          {/* Hint polygon overlay when unlocked and visible */}
          {hintPolygonVisible && hintStatus && hintStatus.status === 'success' && hintStatus.polygon && hintStatus.polygon.length > 0 && (
            <LeafletPolygon
              positions={hintStatus.polygon.map(p => [p.lat, p.lng])}
              pathOptions={{
                color: '#f59e0b',
                fillColor: '#f59e0b',
                fillOpacity: 0.2,
                weight: 3,
                dashArray: '8, 4'
              }}
            />
          )}
        </MapContainer>

        {/* Message when original point is hidden - centered on screen, auto-hide after 2s */}
        {mapExpanded && result && !showOriginalPoint && showMessage && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '1.5rem 2rem',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            zIndex: 1000,
            maxWidth: '90%',
            textAlign: 'center',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: '#2d3748',
            border: '2px solid #667eea',
            opacity: showMessage ? 1 : 0,
            transition: 'opacity 0.5s ease-out',
            pointerEvents: showMessage ? 'auto' : 'none'
          }}>
            {t("guessGame.originalPointHidden")}
          </div>
        )}

        {/* Map Search and Type Selector - top left corner */}
        {mapExpanded && !result && (
          <div style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            zIndex: 1000
          }}>
            {/* Search icon button - always visible at top */}
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #f59e0b',
              borderRadius: '20px',
              padding: '6px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              width: 'fit-content'
            }}>
              <button
                onClick={() => {
                  setShowMapSearch(!showMapSearch);
                  if (showMapSearch) {
                    setSearchSuggestions([]);
                    setMapSearchQuery('');
                  }
                }}
                data-testid="map-search-toggle"
                title={t('guessGame.locationSearch')}
                style={{
                  background: showMapSearch ? 'rgba(255,255,255,0.9)' : 'transparent',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#92400e'
                }}
              >
                <Search size={18} />
              </button>
            </div>
            
            {/* Search input with suggestions - appears when icon clicked */}
            {showMapSearch && (
              <div style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: '1px solid #f59e0b',
                borderRadius: '16px',
                padding: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                width: '280px'
              }}>
                <input
                  type="text"
                  value={mapSearchQuery}
                  onChange={async (e) => {
                    const query = e.target.value;
                    setMapSearchQuery(query);
                    if (query.trim().length >= 2) {
                      try {
                        const resp = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`);
                        const data = await resp.json();
                        setSearchSuggestions(data || []);
                      } catch (err) {
                        console.error("Search error:", err);
                        setSearchSuggestions([]);
                      }
                    } else {
                      setSearchSuggestions([]);
                    }
                  }}
                  placeholder={t('guessGame.enterNamePlace')}
                  data-testid="map-search-input"
                  autoFocus
                  style={{
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'rgba(255,255,255,0.95)',
                    fontSize: '14px',
                    width: '100%',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                {/* Suggestions dropdown */}
                {searchSuggestions.length > 0 && (
                  <div style={{
                    marginTop: '8px',
                    background: 'rgba(255,255,255,0.95)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {searchSuggestions.map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          if (mapRef.current) {
                            mapRef.current.setView([parseFloat(item.lat), parseFloat(item.lon)], 12);
                          }
                          setMapSearchQuery(item.display_name.split(',')[0]);
                          setSearchSuggestions([]);
                          setShowMapSearch(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: idx < searchSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                          fontSize: '13px',
                          color: '#333',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#fef3c7'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >
                        <div style={{ fontWeight: '500' }}>{item.display_name.split(',')[0]}</div>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          {item.display_name.split(',').slice(1, 3).join(',')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Map type buttons */}
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #f59e0b',
              borderRadius: '20px',
              padding: '6px',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              width: 'fit-content'
            }}>
              <button
                onClick={() => setMapType("streets")}
                data-testid="map-type-streets"
                title={t("guessGame.mapScheme")}
                style={{
                  background: mapType === "streets" ? 'rgba(255,255,255,0.9)' : 'transparent',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#92400e'
                }}
              >
                <MapIcon size={18} />
              </button>
              <button
                onClick={() => setMapType("satellite")}
                data-testid="map-type-satellite"
                title={t("guessGame.mapSatellite")}
                style={{
                  background: mapType === "satellite" ? 'rgba(255,255,255,0.9)' : 'transparent',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#92400e'
                }}
              >
                <Satellite size={18} />
              </button>
              <button
                onClick={() => setMapType("hybrid")}
                data-testid="map-type-hybrid"
                title={t("guessGame.mapHybrid")}
                style={{
                  background: mapType === "hybrid" ? 'rgba(255,255,255,0.9)' : 'transparent',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#92400e'
                }}
              >
                <Layers size={18} />
              </button>
            </div>

            {/* Hint button + polygon toggle */}
            {hintStatus && hintStatus.has_polygon && user && !result && hintMenuOpen && hintStatus.status === 'available' && (
              <div data-testid="hint-menu-dropdown" style={{
                background: 'rgba(255,255,255,0.97)',
                borderRadius: '14px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                overflow: 'hidden',
                minWidth: '220px',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                  {t('guessGame.getHint')}
                </div>
                <button
                  data-testid="hint-quiz-option"
                  onClick={() => { setHintMenuOpen(false); handleRequestHint(); }}
                  style={{
                    width: '100%', padding: '12px 14px', background: 'transparent', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                    fontSize: '13px', color: '#1e293b', textAlign: 'left', borderBottom: '1px solid #f0f0f0'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: '18px' }}>&#128221;</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('guessGame.free')}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{t('guessGame.answersQuestionsNews')}</div>
                  </div>
                </button>
                {(hintStatus.free_hint_timer_minutes || 0) > 0 && (
                  <button
                    data-testid="hint-timer-option"
                    onClick={async () => {
                      setHintMenuOpen(false);
                      try {
                        const r = await axios.post(
                          `${API}/hints/timer-start/${panorama.id}`,
                          {},
                          { headers: getAuthHeader() }
                        );
                        if (r.data?.already_unlocked) {
                          const st = await axios.get(`${API}/hints/status/${panorama.id}`, { headers: getAuthHeader() });
                          setHintStatus(st.data);
                          return;
                        }
                        setHintTimerSeconds(r.data?.required_seconds || (hintStatus.free_hint_timer_minutes * 60));
                        setHintTimerOpen(true);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Не удалось запустить таймер');
                      }
                    }}
                    style={{
                      width: '100%', padding: '12px 14px', background: 'transparent', border: 'none',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                      fontSize: '13px', color: '#1e293b', textAlign: 'left', borderBottom: '1px solid #f0f0f0'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '18px' }}>&#9203;</span>
                    <div>
                      <div style={{ fontWeight: 600 }}>{t('guessGame.freeWithTimer') || 'Бесплатно с таймером'}</div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                        {(t('guessGame.waitMinutes') || 'Подождите {n} мин').replace('{n}', String(hintStatus.free_hint_timer_minutes))}
                      </div>
                    </div>
                  </button>
                )}
                {hintStatus.hint_cost > 0 && (
                  <button
                    data-testid="hint-buy-option"
                    onClick={handleBuyHintWithPoints}
                    style={{
                      width: '100%', padding: '12px 14px', background: 'transparent', border: 'none',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                      fontSize: '13px', color: '#1e293b', textAlign: 'left'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '18px' }}>&#11088;</span>
                    <div>
                      <div style={{ fontWeight: 600 }}>{t('guessGame.points2')}</div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Стоимость: {hintStatus.hint_cost} баллов</div>
                    </div>
                  </button>
                )}
              </div>
            )}
            {hintStatus && hintStatus.has_polygon && user && !result && (
              <div style={{
                background: hintStatus.status === 'success'
                  ? 'linear-gradient(135deg, #d1fae5 0%, #6ee7b7 100%)'
                  : hintStatus.status === 'failed'
                    ? 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'
                    : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: hintStatus.status === 'success'
                  ? '1px solid #10b981'
                  : hintStatus.status === 'failed'
                    ? '1px solid #94a3b8'
                    : '1px solid #f59e0b',
                borderRadius: '20px',
                padding: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                width: 'fit-content',
                opacity: hintStatus.status === 'failed' ? 0.5 : 1
              }}>
                {hintStatus.status === 'available' ? (
                  <button
                    onClick={() => setHintMenuOpen(v => !v)}
                    disabled={hintLoading}
                    data-testid="hint-btn"
                    title={t('guessGame.hint')}
                    style={{
                      background: hintMenuOpen ? 'rgba(255,255,255,0.9)' : 'transparent',
                      border: 'none',
                      borderRadius: '14px',
                      padding: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#92400e',
                      opacity: hintLoading ? 0.5 : 1
                    }}
                  >
                    <HintPolygonIcon size={18} color="#92400e" />
                  </button>
                ) : hintStatus.status === 'failed' ? (
                  <button
                    data-testid="hint-failed-badge"
                    title={t('guessGame.hintNoLongerAvailable')}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '14px',
                      padding: '8px',
                      cursor: 'default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94a3b8'
                    }}
                  >
                    <HintPolygonIcon size={18} color="#94a3b8" />
                  </button>
                ) : (
                  <button
                    data-testid="hint-unlocked-badge"
                    title={t('guessGame.hintActive')}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '14px',
                      padding: '8px',
                      cursor: 'default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#065f46'
                    }}
                  >
                    <HintPolygonIcon size={18} color="#065f46" />
                  </button>
                )}
                {hasRole('admin') && hintStatus.status !== 'available' && (
                  <button data-testid="hint-reset-btn" title={t('guessGame.resetAdmin')}
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/hints/reset/${panorama?.id}`, {}, { headers: getAuthHeader() });
                        const r = await axios.get(`${API}/hints/status/${panorama.id}`, { headers: getAuthHeader() });
                        setHintStatus(r.data);
                        toast.success(t('guessGame.dropped'));
                      } catch(e) { toast.error(t('guessGame.mistake')); }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '14px',
                      padding: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: hintStatus.status === 'success' ? '#065f46' : '#92400e'
                    }}
                  >
                    <RotateCcw size={14} />
                  </button>
                )}
                {/* Polygon visibility toggle - shown when hint is unlocked */}
                {hintStatus.status === 'success' && hintStatus.polygon && hintStatus.polygon.length > 0 && (
                  <button
                    onClick={() => setHintPolygonVisible(v => !v)}
                    data-testid="hint-polygon-toggle"
                    title={hintPolygonVisible ? "Скрыть полигон" : "Показать полигон"}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '14px',
                      padding: '6px 8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0px',
                      color: '#065f46',
                      position: 'relative'
                    }}
                  >
                    <Eye size={14} style={{ opacity: hintPolygonVisible ? 1 : 0.35 }} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="floating-result-display" data-testid="result-display">
            <div className="result-content">
              <div className="points-text" data-testid="points-display">
                {result.points.toLocaleString()} {t("guessGame.points")}
              </div>
              <div className="distance-subtext" data-testid="distance-display">
                {result.distance_display
                  ? `${t("guessGame.closerThan") || "ближе"} ${result.distance_display} ${t("guessGame.meters")}`
                  : result.distance < 1000
                    ? `${Math.round(result.distance)} ${t("guessGame.meters")}`
                    : `${(result.distance / 1000).toFixed(1)} ${t("guessGame.kilometers")}`}
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Unified Game Controls - always visible, positioned over the expanded view */}
      <button
        onClick={() => {
          const viewUserId = searchParams.get('viewUserId');
          if (viewUserId) {
            // Return to user results if viewing another user's result
            navigate(`/${gameId}/user-results/${viewUserId}`);
            return;
          }
          // If this panorama was opened via a video timecode, behave like
          // "next" so the user returns to the video at the same position,
          // and keep browser-native fullscreen alive across navigation.
          const tcBack = (gameTimecodes || []).find(t => t.panorama_id === panoramaId);
          if (tcBack && (gameTimecodes || []).length > 0) {
            const wasFsBack = sessionStorage.getItem('playerWasFullscreen') === 'true';
            sessionStorage.setItem('interactivePlayerResumeState', JSON.stringify({
              isActive: true,
              gameId,
              currentTime: (tcBack.timecode || tcBack.time) + 1,
              lastNavTime: tcBack.timecode || tcBack.time,
              wasFullscreen: wasFsBack
            }));
            if (wasFsBack && !document.fullscreenElement) {
              document.documentElement.requestFullscreen().catch(() => {});
            }
          }
          navigate(gameId ? `/games/${gameId}` : '/');
        }}
        className="floating-back-btn unified-control"
        data-testid="back-home-btn"
        title={t("guessGame.backToGame")}
      >
        <ArrowLeft size={18} />
        {t("guessGame.back")}
      </button>

      {!result && !isViewingOtherUser && (gameData?.game_mode !== 'objects_only' || hasRole(['admin', 'editor'])) && (
        <button
          onClick={handleGuess}
          disabled={!guessMarker}
          className="floating-guess-btn unified-control"
          data-testid="guess-btn"
        >
          {hasPersistentAnswer ? t('guessGame.guess') : (guessMarker ? t('guessGame.guess') : t('guessGame.selectPointOnMap'))}
        </button>
      )}

      {/* Show "Next" or "Close" button after answer is submitted */}
      {result && !isViewingOtherUser && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Check if this panorama was opened via video timecode
            const tc = gameTimecodes.find(t => t.panorama_id === panoramaId);
            const hasVideoMode = tc && gameTimecodes.length > 0;
            const nextPanoramaId = findNextUnansweredPanorama();

            if (hasVideoMode && nextPanoramaId) {
              // Video mode: return to video, resume after current timecode
              const wasFs = sessionStorage.getItem('playerWasFullscreen') === 'true';
              sessionStorage.setItem('interactivePlayerResumeState', JSON.stringify({
                isActive: true,
                gameId,
                currentTime: (tc.timecode || tc.time) + 1,
                lastNavTime: tc.timecode || tc.time,
                wasFullscreen: wasFs
              }));
              // Keep browser-native fullscreen alive across the SPA navigation.
              // We are inside a click handler — this is a fresh user gesture,
              // so the request is permitted. No-op if already in FS.
              if (wasFs && !document.fullscreenElement) {
                document.documentElement.requestFullscreen().catch(() => {});
              }
              navigate(`/${gameId}`);
            } else if (nextPanoramaId) {
              navigate(`/${gameId}/guess/${nextPanoramaId}`);
            } else {
              navigate(`/${gameId}`);
            }
          }}
          className="floating-guess-btn unified-control"
          data-testid="next-btn"
          style={{
            background: findNextUnansweredPanorama() ? '#10b981' : '#ef4444'
          }}
        >
          {(() => {
            const nextId = findNextUnansweredPanorama();
            if (!nextId) return t('guessGame.close');
            const tc = gameTimecodes.find(t => t.panorama_id === panoramaId);
            return (tc && gameTimecodes.length > 0) ? "Далее ▶" : t('guessGame.next');
          })()}
        </button>
      )}

      {/* Hint Purchase Confirmation Dialog */}

      {/* Hint button - available (placeholder - moved to map controls) */}

      {/* Hint button - failed (placeholder - moved to map controls) */}

      {/* Hint status - success (placeholder - moved to map controls) */}

      {hintDialog.open && hintDialog.hotspot && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "1.5rem",
            maxWidth: "400px",
            width: "90%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            textAlign: "center"
          }}>
            <div style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1rem"
            }}>
              <Eye size={28} color="white" />
            </div>
            <h3 style={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              marginBottom: "0.5rem",
              color: "#1a1a1a"
            }}>
              {t('guessGame.shouldShowObject')}
            </h3>
            <p style={{
              fontSize: "1rem",
              color: "#666",
              marginBottom: "0.5rem"
            }}>
              <strong>{getTranslatedText(hintDialog.hotspot.name_translations, language, hintDialog.hotspot.name)}</strong>
            </p>
            <p style={{
              fontSize: "0.9rem",
              color: "#888",
              marginBottom: "1.5rem"
            }}>
              {t('guessGame.debited')} <strong style={{ color: "#f59e0b" }}>{t('guessGame.100Points')}</strong>
            </p>
            <div style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "center"
            }}>
              <button
                onClick={() => setHintDialog({ open: false, hotspot: null })}
                style={{
                  padding: "0.75rem 1.5rem",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: "white",
                  color: "#666",
                  fontSize: "1rem",
                  cursor: "pointer",
                  fontWeight: "500"
                }}
              >
                {t('guessGame.cancel')}
              </button>
              <button
                onClick={() => handleBuyHint(hintDialog.hotspot)}
                style={{
                  padding: "0.75rem 1.5rem",
                  borderRadius: "8px",
                  border: "none",
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "white",
                  fontSize: "1rem",
                  cursor: "pointer",
                  fontWeight: "600"
                }}
              >
                {t('guessGame.show100')}
              </button>
            </div>
          </div>
        </div>
      )}

      {hintTimerOpen && panorama && (
        <HintTimerOverlay
          panoramaId={panorama.id}
          totalSeconds={hintTimerSeconds}
          t={t}
          onClose={() => setHintTimerOpen(false)}
          onUnlocked={async () => {
            setHintTimerOpen(false);
            try {
              const st = await axios.get(`${API}/hints/status/${panorama.id}`, { headers: getAuthHeader() });
              setHintStatus(st.data);
              // Mirror the post-purchase behaviour: zoom the map to the
              // revealed polygon so the player immediately sees the hint.
              if (st.data && st.data.polygon && st.data.polygon.length > 0) {
                const bounds = L.latLngBounds(st.data.polygon.map(p => [p.lat, p.lng]));
                setTimeout(() => {
                  if (mapRef.current) {
                    mapRef.current.invalidateSize();
                    mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 18, animate: true });
                  }
                }, 600);
              }
            } catch (_) {}
          }}
        />
      )}

            {/* Email Verification Modal */}
      {showVerificationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: '#1a1a1a'
            }}>
              {t("guessGame.confirmEmail")}
            </h2>
            <p style={{
              fontSize: '1rem',
              color: '#666',
              marginBottom: '1.5rem',
              lineHeight: '1.6'
            }}>
              {t('guessGame.participateGameMustConfirm')} <strong>{user?.email}</strong>.
            </p>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              marginBottom: '1.5rem'
            }}>
              {t('guessGame.notReceiveEmailCheck')}
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowVerificationModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  backgroundColor: 'white',
                  color: '#666',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                {t('guessGame.close2')}
              </button>
              <button
                onClick={handleResendVerification}
                disabled={resendingEmail}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#667eea',
                  color: 'white',
                  fontSize: '1rem',
                  cursor: resendingEmail ? 'not-allowed' : 'pointer',
                  opacity: resendingEmail ? 0.6 : 1
                }}
              >
                {resendingEmail ? 'Отправка...' : 'Отправить повторно'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Not Started Overlay - blocks gameplay until game starts */}
      {gameCountdown && !gameCountdown.expired && !isViewingOtherUser && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002
          }}
          data-testid="game-countdown-overlay"
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2.5rem',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1rem'
            }}>
              ⏱️
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: '#1a1a1a'
            }}>
              {t('guessGame.gameHasnTStarted')}
            </h2>
            <p style={{
              fontSize: '1rem',
              color: '#666',
              marginBottom: '1.5rem',
              lineHeight: '1.6'
            }}>
              {t('guessGame.thereSStillTime')}
            </p>
            <div style={{
              backgroundColor: '#f0f9ff',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                color: '#1e40af',
                fontFamily: 'monospace'
              }}>
                {gameCountdown.days > 0 && `${gameCountdown.days}д `}
                {String(gameCountdown.hours).padStart(2, '0')}:
                {String(gameCountdown.minutes).padStart(2, '0')}:
                {String(gameCountdown.seconds).padStart(2, '0')}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
                📅 Старт: {new Date(gameCountdown.deadline).toLocaleString('ru-RU', {
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
            <button
              onClick={() => navigate(`/${gameId}`)}
              style={{
                padding: '0.875rem 1.75rem',
                borderRadius: '8px',
                border: '2px solid #ddd',
                backgroundColor: 'white',
                color: '#666',
                fontSize: '1rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              {t('guessGame.backGame')}
            </button>
          </div>
        </div>
      )}

      {/* Paid Game Activation Overlay - blocks gameplay for non-activated users */}
      {activationStatus && activationStatus.requires_points && !activationStatus.activated && !isViewingOtherUser && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100001
          }}
          data-testid="activation-overlay"
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2.5rem',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1rem'
            }}>
              ⭐
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              color: '#1a1a1a'
            }}>
              {t("game.paidGameTitle")}
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#666',
              marginBottom: '1.5rem',
              lineHeight: '1.6'
            }}>
              {t("game.activationRequired")}
            </p>
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: '1.25rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                {t("game.activationCost")}
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
                {activationStatus.points_required} ⭐
              </div>
              {user && (
                <div style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.5rem' }}>
                  {t("game.yourBalance")}: {activationStatus.user_points || 0} {t("common.points")}
                </div>
              )}
            </div>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => navigate(`/${gameId}`)}
                style={{
                  padding: '0.875rem 1.75rem',
                  borderRadius: '8px',
                  border: '2px solid #ddd',
                  backgroundColor: 'white',
                  color: '#666',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                ← {t("game.backToGame")}
              </button>
            </div>
            {!user && (
              <p style={{
                fontSize: '0.85rem',
                color: '#888',
                marginTop: '1.5rem'
              }}>
                <a href="/game/login" style={{ color: '#667eea' }}>{t("auth.login")}</a>, {t("game.loginToActivate")}
              </p>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default GuessGame;